/**
 * NAOKO AI Recommendations Widget
 * Vanilla JS — zero external dependencies — loaded with defer.
 *
 * Handles layers 2 and 3 of the latency architecture:
 *   Layer 2 (~50ms)  — sessionStorage cache: instant swap on repeat PDP views
 *   Layer 3 (~860ms) — API fetch to /recommend on cache miss
 *
 * Layer 1 (0ms) is handled server-side in snippets/ai-recs-widget.liquid
 * via the ai_recs.recommendations metafield.
 *
 * CLS = 0: swapWidget() replaces ONLY textContent/src/href inside
 * existing data-ai-* elements — never innerHTML on the wrapper.
 *
 * FOUC prevention: shimmer → personalized is perceived as natural loading.
 * Errors degrade silently — widget stays in its current state (shimmer or ready).
 */

(function () {
  'use strict';

  // ── Season helper ──────────────────────────────────────────────────────

  function getSeason() {
    var m = new Date().getMonth() + 1;
    if (m >= 3 && m <= 5)  return 'wiosna';
    if (m >= 6 && m <= 8)  return 'lato';
    if (m >= 9 && m <= 11) return 'jesien';
    return 'zima';
  }

  // ── Customer context — injected by theme.liquid ────────────────────────
  // window.__naokoCustomerId: Shopify customer.id (number) or null
  // window.__naokoOrderCount: customer.orders_count (number, 0 for guests)

  function getCustomerId() {
    return window.__naokoCustomerId || null;
  }

  function getOrderCount() {
    return window.__naokoOrderCount || 0;
  }

  // ── API URL — priority: section data attr > window global > localhost ──

  function getApiUrl(widgetEl) {
    var sectionEl = widgetEl.closest('[data-ai-api-url]');
    return (sectionEl && sectionEl.dataset.aiApiUrl)
      || window.__naokoApiUrl
      || 'http://localhost:3000';
  }

  // ── Language / country from Shopify globals ────────────────────────────

  function getLanguage() {
    var lang = document.documentElement.lang;
    return (lang && lang.split('-')[0].toLowerCase()) || 'pl';
  }

  function getCountry() {
    return (window.Shopify && window.Shopify.country)
      || document.documentElement.dataset.country
      || 'PL';
  }

  // ── sessionStorage key — includes season to prevent stale cross-season cache

  function getSessionKey(productType, customerId) {
    var season = getSeason();
    if (customerId) {
      return 'naoko_rec_personal_' + customerId + '_' + productType + '_' + season;
    }
    return 'naoko_rec_' + productType + '_' + season;
  }

  // ── Request payload builder ────────────────────────────────────────────

  function buildRequest(productId) {
    return {
      customer_id:  getCustomerId(),
      product_id:   productId,
      order_count:  getOrderCount(),
      intent: (function() {
        var i = window.naokoIntent ? window.naokoIntent.getIntent() : null;
        return {
          color:      (i && i.color)      || null,
          size:       (i && i.size)       || null,
          style:      (i && i.style)      || null,
          confidence: (i && i.confidence) || 0
        };
      })(),
      context: {
        country:      getCountry(),
        season:       getSeason(),
        language:     getLanguage(),
        utm_campaign: new URLSearchParams(location.search).get('utm_campaign')
      }
    };
  }

  // ── Widget swap — CLS = 0 ─────────────────────────────────────────────
  // Replaces ONLY content inside existing data-ai-* elements.
  // Never modifies innerHTML of the .ai-recs wrapper.
  // Identical DOM structure is required — maintained by the Liquid snippet.

  function swapWidget(widgetEl, data) {
    if (!data || !data.products || !data.products.length) return;

    var cards = widgetEl.querySelectorAll('[data-ai-card]');

    data.products.forEach(function (product, i) {
      var card = cards[i];
      if (!card) return;

      // URLs — both link elements in the card share the same href
      card.querySelectorAll('[data-ai-url]').forEach(function (el) {
        el.href = product.url || '#';
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-hidden');
      });

      // Image
      var img = card.querySelector('[data-ai-image]');
      if (img && product.image) {
        img.src = product.image;
        img.alt = product.title || '';
      }

      // Title
      var title = card.querySelector('[data-ai-title]');
      if (title) title.textContent = product.title || '';

      // Price — append currency symbol
      var price = card.querySelector('[data-ai-price]');
      if (price) price.textContent = product.price ? (product.price + ' zł') : '';

      // AI reason — max 10 words, in user's language (set by backend)
      var reason = card.querySelector('[data-ai-reason]');
      if (reason) reason.textContent = product.reason || '';
    });

    widgetEl.dataset.state = 'personalized';
  }

  // ── Click tracking (sendBeacon) ────────────────────────────────────────
  // Non-blocking — survives page navigation.
  // POST /api/track with variantId, position, productId, sourceProductId.

  function setupTracking(widgetEl, sourceProductId, apiUrl) {
    widgetEl.addEventListener('click', function (e) {
      var card = e.target.closest('[data-ai-card]');
      if (!card) return;

      var cards    = Array.from(widgetEl.querySelectorAll('[data-ai-card]'));
      var position = cards.indexOf(card) + 1;
      if (!position) return;

      var linkEl      = card.querySelector('[data-ai-url]');
      var href        = linkEl ? linkEl.href : '';
      var productMatch = href.match(/\/products\/([^?#/]+)/);
      var recProductId = productMatch ? productMatch[1] : '';

      var variantId = widgetEl.dataset.variantId || 'unknown';

      var payload = JSON.stringify({
        variantId:       variantId,
        productId:       recProductId,
        position:        position,
        sourceProductId: sourceProductId,
        customerId:      getCustomerId()
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          apiUrl + '/api/track',
          new Blob([payload], { type: 'application/json' })
        );
      }
    });
  }

  // ── Main widget init ───────────────────────────────────────────────────

  function initWidget(widgetEl) {
    var productId   = widgetEl.dataset.productId;
    var productType = widgetEl.dataset.productType;

    if (!productId) return;

    var apiUrl     = getApiUrl(widgetEl);
    var customerId = getCustomerId();
    var sessionKey = getSessionKey(productType, customerId);

    setupTracking(widgetEl, productId, apiUrl);

    // ── Product color from metafield (023_kolor_wiodacy) ───────────────
    // Liquid injects the dominant color into data-ai-color at render time.
    // We register it as a color signal immediately — no click required.
    // This gives archetype-detector a head start before the user interacts.
    var productColor = widgetEl.dataset.aiColor;
    if (productColor && window.naokoIntent) {
      window.naokoIntent.addSignal('color', productColor);
    }

    // ── Layer 2: sessionStorage cache ──────────────────────────────────
    // Already-personalized widgets don't need an API call.
    // Skip if Liquid already set data-state="ready" (metafield hit) — the
    // sessionStorage data would have the same generic content anyway.
    // Only skip API if we have personalized cache (customer-keyed session key).

    try {
      var cached = sessionStorage.getItem(sessionKey);
      if (cached) {
        var data = JSON.parse(cached);
        swapWidget(widgetEl, data);
        return; // cache hit — done
      }
    } catch (e) {
      // sessionStorage unavailable or JSON parse error — fall through to Layer 3
    }

    // ── Layer 3: API fetch ─────────────────────────────────────────────
    // Only fetch if backend credentials are configured.
    // If apiUrl is localhost and we're in production, fetch will fail silently.

    var request = buildRequest(productId);

    fetch(apiUrl + '/recommend', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(request)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.products || !data.products.length) return;

        // Store variantId on element for tracking
        if (data.variantId) widgetEl.dataset.variantId = data.variantId;

        // Cache in sessionStorage — avoids re-fetching on next PDP view this session
        try {
          sessionStorage.setItem(sessionKey, JSON.stringify(data));
        } catch (e) {
          // sessionStorage full or unavailable — still swap the widget
        }

        swapWidget(widgetEl, data);
      })
      .catch(function (err) {
        // Silent degradation — widget stays in current state (shimmer or metafield ready).
        // Never show error UI — it would be worse than the shimmer.
        console.warn('[ai-recs] fetch failed:', err.message);
      });
  }

  // ── Boot ───────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[id^="ai-recs-"]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
